import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { createOrderSchema, formatCityLine, formatNip, type Vendor } from '@faktorplus/shared';
import { useProjects, useVendors } from '../../api/dictionaries';
import { useCompany } from '../../api/company';
import { useSendOrder } from '../../api/orders';
import { useCurrentUser } from '../../auth/auth-provider';
import { Button } from '../../components/ui/button';
import { Field, Input, Select, Textarea } from '../../components/ui/form';
import { ErrorBox, LoadingBlock, Panel } from '../../components/ui/feedback';
import { ApiError } from '../../lib/api-client';
import {
  OrderItemsEditor,
  toOrderItems,
  EMPTY_ORDER_ITEM,
  type DraftOrderItem,
} from './order-items-editor';
import pageStyles from '../../components/layout/page.module.css';
import styles from './order-page.module.css';

/**
 * The site manager's order pad.
 *
 * Replaces the phone call to the merchant, so it asks for exactly what that
 * call contains — what, where, and who to ring on site — and adds the one
 * thing the call always forgets: the invoice details, which come from the
 * deployment's configuration and are not editable here by anyone.
 *
 * Submitting queues the message rather than sending it, so the form does not
 * claim delivery it cannot vouch for: it hands over to the history, which is
 * the screen that knows whether the order left.
 */
export function OrderPage() {
  const user = useCurrentUser();
  const navigate = useNavigate();
  const { data: vendors = [] } = useVendors();
  const { data: projects = [] } = useProjects();
  const send = useSendOrder();

  const [projectId, setProjectId] = useState('');
  const [vendorId, setVendorId] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [items, setItems] = useState<DraftOrderItem[]>([{ ...EMPTY_ORDER_ITEM }]);
  const [street, setStreet] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [city, setCity] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const selectedVendor = vendors.find((vendor) => vendor.id === vendorId) ?? null;

  // A manager running a single site never has a choice to make; asking them to
  // pick the only option is a click that teaches nothing.
  const selectedProjectId = projectId || (projects.length === 1 ? projects[0]!.id : '');

  function pickVendor(nextId: string) {
    setVendorId(nextId);

    // Picking a merchant fills their order address; a merchant without one
    // leaves the field alone rather than wiping what was already typed.
    const email = vendors.find((vendor) => vendor.id === nextId)?.email;
    if (email) setRecipientEmail(email);
  }

  async function handleSubmit() {
    setErrors({});

    const parsed = createOrderSchema.safeParse({
      projectId: selectedProjectId,
      vendorId: vendorId || null,
      recipientEmail,
      items: toOrderItems(items),
      delivery: { street, postalCode, city, contactPhone },
      notes: notes.trim() || null,
    });

    if (!parsed.success) {
      setErrors(collectErrors(parsed.error.issues));
      return;
    }

    const order = await send.mutateAsync(parsed.data);
    navigate('/zamowienia', { state: { placedOrderId: order.id } });
  }

  const itemsError = firstItemError(errors);

  return (
    <>
      <header className={pageStyles.header}>
        <h1 className={pageStyles.title}>Złóż zamówienie</h1>
        <span className={pageStyles.subtitle}>
          Wiadomość idzie prosto do kontrahenta, podpisana Twoim nazwiskiem
        </span>
        <div className={pageStyles.actions}>
          <Link to="/zamowienia">
            <Button variant="ghost">Moje zamówienia</Button>
          </Link>
        </div>
      </header>

      <div className={styles.form}>
        <Panel title="Do kogo">
          <Field label="Kontrahent" hint="Nie musisz wybierać — adres możesz wpisać ręcznie.">
            {(id) => (
              <Select id={id} value={vendorId} onChange={(event) => pickVendor(event.target.value)}>
                <option value="">— wpisz adres ręcznie</option>
                {vendors.map((vendor) => (
                  <option key={vendor.id} value={vendor.id}>
                    {vendorOptionLabel(vendor)}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field
            label="Adres e-mail zamówień"
            error={errors.recipientEmail}
            hint={
              selectedVendor && !selectedVendor.email
                ? `${selectedVendor.name} nie ma zapisanego adresu do zamówień — wpisz go poniżej.`
                : undefined
            }
          >
            {(id, hasError) => (
              <Input
                id={id}
                type="email"
                hasError={hasError}
                placeholder="zamowienia@sklad.pl"
                value={recipientEmail}
                onChange={(event) => setRecipientEmail(event.target.value)}
              />
            )}
          </Field>
        </Panel>

        <Panel title="Co zamawiasz">
          <Field label="Pozycje" error={itemsError}>
            {() => <OrderItemsEditor items={items} onChange={setItems} />}
          </Field>
        </Panel>

        <Panel title="Gdzie dostarczyć">
          <Field
            label="Budowa"
            error={errors.projectId}
            hint="Zamówienie zostanie zapisane na tej budowie i wymienione w wiadomości."
          >
            {(id, hasError) => (
              <Select
                id={id}
                hasError={hasError}
                value={selectedProjectId}
                onChange={(event) => setProjectId(event.target.value)}
              >
                <option value="">— wybierz budowę</option>
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field label="Ulica i numer" error={errors['delivery.street']}>
            {(id, hasError) => (
              <Input
                id={id}
                hasError={hasError}
                placeholder="ul. Mroźna 12"
                value={street}
                onChange={(event) => setStreet(event.target.value)}
              />
            )}
          </Field>

          <div className={styles.pair}>
            <Field label="Kod pocztowy" error={errors['delivery.postalCode']}>
              {(id, hasError) => (
                <Input
                  id={id}
                  hasError={hasError}
                  placeholder="32-800"
                  inputMode="numeric"
                  value={postalCode}
                  onChange={(event) => setPostalCode(event.target.value)}
                />
              )}
            </Field>

            <Field label="Miasto" error={errors['delivery.city']}>
              {(id, hasError) => (
                <Input
                  id={id}
                  hasError={hasError}
                  placeholder="Brzesko"
                  value={city}
                  onChange={(event) => setCity(event.target.value)}
                />
              )}
            </Field>
          </div>

          <Field
            label="Numer kontaktowy"
            error={errors['delivery.contactPhone']}
            hint="Numer, pod który kierowca zadzwoni przed rozładunkiem."
          >
            {(id, hasError) => (
              <Input
                id={id}
                type="tel"
                hasError={hasError}
                placeholder="600 100 200"
                value={contactPhone}
                onChange={(event) => setContactPhone(event.target.value)}
              />
            )}
          </Field>

          <Field label="Uwagi">
            {(id) => (
              <Textarea
                id={id}
                rows={3}
                placeholder="np. dostawa do 12:00, wjazd od strony bramy nr 2"
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
              />
            )}
          </Field>
        </Panel>

        <InvoiceDetailsPanel />

        {send.error && (
          <ErrorBox>
            {send.error instanceof ApiError
              ? send.error.message
              : 'Nie udało się wysłać zamówienia.'}
          </ErrorBox>
        )}

        <div className={styles.submit}>
          <span className={styles.submitNote}>
            Wyślemy jako {user.fullName} · {user.email}
          </span>
          <Button variant="primary" isLoading={send.isPending} onClick={() => void handleSubmit()}>
            Wyślij zamówienie
          </Button>
        </div>
      </div>
    </>
  );
}

/**
 * The invoice details, shown so the manager knows what the merchant will get
 * — and read-only, because they come from the deployment's configuration.
 * There is no endpoint that changes them.
 */
function InvoiceDetailsPanel() {
  const { data: company, isLoading, error } = useCompany();

  return (
    <Panel title="Dane do faktury">
      {isLoading && <LoadingBlock />}
      {error && <ErrorBox>Nie udało się pobrać danych firmy.</ErrorBox>}

      {company && (
        <>
          <p className={styles.invoiceLead}>
            Zamówienie <b>na fakturę VAT</b> z odroczonym terminem płatności. Kontrahent
            dostanie te dane w treści wiadomości:
          </p>
          <div className={styles.invoiceBox}>
            <p className={styles.invoiceName}>{company.name}</p>
            <p>{company.street}</p>
            <p>{formatCityLine(company)}</p>
            <p className={styles.invoiceNip}>NIP: {formatNip(company.nip)}</p>
          </div>
          <p className={styles.invoiceNote}>
            Dane pochodzą z konfiguracji systemu i nie da się ich tu zmienić.
          </p>
        </>
      )}
    </Panel>
  );
}

function vendorOptionLabel(vendor: Vendor): string {
  return vendor.email ? `${vendor.name} — ${vendor.email}` : `${vendor.name} — brak adresu`;
}

/** Zod paths become the keys the fields look themselves up by. */
function collectErrors(issues: { path: PropertyKey[]; message: string }[]): Record<string, string> {
  const collected: Record<string, string> = {};

  for (const issue of issues) {
    const key = issue.path.map(String).join('.');
    collected[key] ??= issue.message;
  }

  return collected;
}

/** Per-row errors have no field of their own, so the section shows the first. */
function firstItemError(errors: Record<string, string>): string | undefined {
  return Object.entries(errors).find(([key]) => key.startsWith('items'))?.[1];
}
